import type { ThemeV2, AtomDefaults, Narrative, I18nConfig, ColorSet } from './theme-v2-types';

export interface ResolvedTheme {
  cssVars: Record<string, string>;
  atomProps: Record<string, AtomDefaults>;
  narrative: Narrative;
  i18n: I18nConfig;
}

export type ThemeColorScheme = 'light' | 'dark';

export const DEFAULT_I18N: I18nConfig = {
  defaultLocale: 'en',
  supportedLocales: ['en'],
  direction: 'ltr'
};

function colorsToVars(set: ColorSet, suffix: '' | '-light' | '-dark' = ''): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, val] of Object.entries(set)) {
    if (!val) continue;
    // Tailwind v4 dual-write: --<key> + --color-<key>
    out[`--${key}${suffix}`] = val;
    out[`--color-${key}${suffix}`] = val;
  }
  return out;
}

export function resolveTheme(theme: ThemeV2): ResolvedTheme {
  const { primitives, atoms, narrative, i18n } = theme;
  const cssVars: Record<string, string> = {
    ...colorsToVars(primitives.colors.light),
    ...colorsToVars(primitives.colors.light, '-light'),
    ...(primitives.colors.dark ? colorsToVars(primitives.colors.dark, '-dark') : {}),
    '--font-sans':    primitives.typography.families.sans,
    '--font-mono':    primitives.typography.families.mono,
    '--font-heading': primitives.typography.families.heading,
    '--spacing':      primitives.spacing.base,
    '--radius':       primitives.radius.base,
    '--shadow-sm':    primitives.elevation.sm,
    '--shadow-md':    primitives.elevation.md,
    '--shadow-lg':    primitives.elevation.lg,
    '--shadow-xl':    primitives.elevation.xl,
    '--duration-fast': primitives.motion.duration.fast,
    '--duration-base': primitives.motion.duration.base,
    '--duration-slow': primitives.motion.duration.slow,
    '--ease-linear':   primitives.motion.ease.linear,
    '--ease-in':       primitives.motion.ease.in,
    '--ease-out':      primitives.motion.ease.out,
    '--ease-in-out':   primitives.motion.ease['in-out']
  };

  // Typography scale → --text-<key> + --leading-<key>
  for (const [key, spec] of Object.entries(primitives.typography.scale ?? {})) {
    if (spec?.size)       cssVars[`--text-${key}`] = spec.size;
    if (spec?.lineHeight) cssVars[`--leading-${key}`] = spec.lineHeight;
  }

  return {
    cssVars,
    atomProps: atoms,
    narrative: narrative ?? {},
    i18n: i18n ?? DEFAULT_I18N
  };
}

/**
 * Projects one stored color scheme onto the canonical CSS variables consumed
 * by Tailwind and the renderers. The resolved artifact keeps both explicit
 * `-light` and `-dark` slots so changing schemes never requires regenerating
 * the theme or the UI Block.
 */
export function resolveThemeCssVarsForScheme(
  theme: Pick<ResolvedTheme, 'cssVars'>,
  colorScheme: ThemeColorScheme,
): Record<string, string> {
  const cssVars = { ...theme.cssVars };
  const suffix = `-${colorScheme}`;
  for (const [key, value] of Object.entries(theme.cssVars)) {
    if (!key.endsWith(suffix)) continue;
    cssVars[key.slice(0, -suffix.length)] = value;
  }
  return cssVars;
}
